<template>
  <div>
    <AlertBanner v-if="jobShared" id="submit-error-alert" :status="1" class="mt-0">
      <div>{{ successMessage() }}</div>
    </AlertBanner>
    <AlertBanner v-if="hasError" id="submit-error-alert" :status="0" class="mt-0">
      <div>Your job could not be shared. Try again.</div>
    </AlertBanner>
    <div class="flex flex-col justify-center p-20 w-full max-w-568 m-auto mt-24">
      <a href="#" class="link--primary mb-24" @click="goBack">
        <div class="inline-block md:pl-0 mt-4"><BackIcon /></div>
        <p class="inline-block ml-8">Back</p>
      </a>
      <h2 class="text-h3">Share the job "{{ jobTitle }}"</h2>
      <PoppinsCard>
        <form id="shareJobForm" @submit.prevent="submitForm">
          <p class="text-grey-body leading-normal">
            Give an existing Sittercity user view-only access to this job and its applicants
          </p>
          <ListCreator
            v-model="sharedEmails"
            labelName="Email"
            class="mb-24"
            buttonText="Add Email"
            type="text"
            errorMessage="Enter a valid email"
            validationError="This email doesn't belong to a Sittercity user"
            :invalidItems="invalidEmails"
            :invalidItemsFormat="invalidFormats"
            @remove-item="unshareEmail"
          />
          <Button
            :value="buttonState()"
            type="submit"
            variant="primary"
            class="w-full sm:w-auto float-right"
            size="medium"
            @submit="submitForm"
          />
        </form>
      </PoppinsCard>
    </div>
  </div>
</template>

<script lang="ts">
import { mapGetters } from 'vuex';
import ListCreator from '@/components/list_creator/ListCreator.vue';
import Button from '@/components/button/Button.vue';
import BackIcon from '@/icons/BackIcon.vue';
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import Card from '@/components/card/Card.vue';
import { FETCH_FC_SHARED_JOBS } from '@/graphql/shared_jobs/query';
import { SHARED_JOBS_MUTATION } from '@/graphql/shared_jobs/mutations';
import shareJobs from '@/lib/share_jobs';

export default {
  name: 'ShareJobsForm',
  components: { ListCreator, Button, BackIcon, AlertBanner, PoppinsCard: Card },
  data() {
    return {
      isLoading: true,
      hasError: false,
      jobShared: false,
      sharedEmails: [''],
      jobId: '',
      jobTitle: '',
      modifiedEmailList: [],
      unshareEmailList: [],
      payload: {},
      newlyAddedEmails: [],
      initialEmails: [''],
      invalidEmails: [''],
      invalidFormats: [''],
    };
  },
  computed: {
    ...mapGetters('account', ['getAccount']),
  },
  watch: {
    sharedEmails() {
      this.modifiedEmailList = [];
      this.makeModifiedEmailList();
    },
  },
  methods: {
    goBack() {
      window.history.back();
    },
    unshareEmail(index) {
      const obj = this.modifiedEmailList.splice(index, 1)[0];
      obj.shared = false;

      if (obj.email && !shareJobs.checkEmptyFields(this.modifiedEmailList) && !shareJobs.validateField(this.modifiedEmailList)) {
        this.unshareEmailList.push(obj);
      }
    },
    makeModifiedEmailList() {
      this.modifiedEmailList = this.sharedEmails.map((email) => ({
        email,
        shared: true,
      }));

      this.newlyAddedEmails = this.modifiedEmailList.filter(
        (modifiedEmailObj) => !this.initialEmails.includes(modifiedEmailObj.email)
      );
    },
    successMessage() {
      if (this.newlyAddedEmails.length > 0) {
        const target =
          this.newlyAddedEmails.length > 1 ? `${this.newlyAddedEmails.length} users` : this.newlyAddedEmails[0].email;
        return `Success: your job was shared with ${target}`;
      }

      if (this.unshareEmailList.length > 0) {
        return `Success: your job was unshared with ${this.unshareEmailList[0].email}`;
      }

      return '';
    },
    buttonState() {
      if (this.initialEmails.length > 0 && this.initialEmails[0] !== '' && !this.isLoading) {
        return 'Save';
      }
      return 'Share';
    },
    submitForm() {
      this.hasError =
        shareJobs.checkEmptyFields(this.modifiedEmailList) ||
        (this.unshareEmailList.length < 1 && this.newlyAddedEmails.length < 1);

      if (!this.hasError) {
        this.shareJobMutation();
        this.successMessage();
      }

      this.payload = {};
      this.modifiedEmailList = [];
    },
    async shareJobMutation() {
      this.isLoading = true;

      this.payload = {
        input: {
          jobId: this.$route.query.job_id,
          emails: [...this.newlyAddedEmails, ...this.unshareEmailList],
        },
      };

      try {
        const response = await this.$apollo.mutate({
          mutation: SHARED_JOBS_MUTATION,
          variables: this.payload,
        });

        this.invalidEmails = [...response.data.sharedJob.invalidEmails];
        this.invalidFormats = [...response.data.sharedJob.invalidEmailFormat];
        this.jobShared = !(this.invalidEmails.length || this.invalidFormats.length);

        this.$emit('updating-shared-emails-table');
      } catch (error) {
        this.hasError = true;
      } finally {
        this.payload = {};
        this.isLoading = false;
      }
    },
  },
  apollo: {
    jobDetails: {
      query: FETCH_FC_SHARED_JOBS,
      variables(): any {
        return {
          jobId: this.$route.query.job_id,
        };
      },
      result({ data, loading, error }) {
        if (!loading) {
          this.sharedEmails = [...data?.jobDetails?.sharedJobEmails];
          this.initialEmails = [...this.sharedEmails];
          this.makeModifiedEmailList();
          this.jobId = data?.jobDetails?.jobId;
          this.jobTitle = data?.jobDetails?.title;
          this.isLoading = false;
          if (this.sharedEmails.length === 0) {
            this.sharedEmails = [''];
          }
        }
        if (error) {
          this.hasError = true;
        }
      },
    },
  },
};
</script>
